import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type Variant = NonNullable<BadgeProps['variant']>;

const STATUS_TO_VARIANT: Record<string, Variant> = {
  // Inventory
  in_stock: 'success',
  low: 'warning',
  out_of_stock: 'destructive',
  reserved: 'info',

  // Finished goods
  available: 'success',
  allocated: 'info',
  dispatched: 'accent',
  on_hold: 'warning',

  // Orders
  draft: 'muted',
  pending: 'warning',
  confirmed: 'info',
  payment_pending: 'warning',
  ready_for_dispatch: 'soft',
  delivered: 'success',
  cancelled: 'muted',
  returned: 'destructive',
  unpaid: 'warning',
  partial: 'info',
  paid: 'success',
  refunded: 'muted',

  // Dispatch
  queued: 'muted',
  picking: 'info',
  packed: 'soft',
  ready: 'soft',
  in_transit: 'accent',
  out_for_delivery: 'info',
  failed: 'destructive',

  // Returns
  requested: 'warning',
  approved: 'info',
  rejected: 'destructive',
  received: 'soft',
  inspected: 'info',
  restocked: 'success',
  closed: 'muted',

  // Refunds
  processing: 'info',
  completed: 'success',

  // Platform — accounts & access
  active: 'success',
  inactive: 'muted',
  invited: 'info',
  revoked: 'destructive',
  expired: 'muted',
  suspended: 'destructive',
  archived: 'muted',

  // Subscription lifecycle
  trial: 'info',
  past_due: 'warning',

  // Support sessions
  denied: 'destructive',

  // Webhooks (delivery) + generic async outcomes
  succeeded: 'success',
  exhausted: 'destructive',
};

const LABELS: Record<string, string> = {
  in_stock: 'In stock',
  low: 'Low',
  out_of_stock: 'Out of stock',
  reserved: 'Reserved',
  available: 'Available',
  allocated: 'Allocated',
  dispatched: 'Dispatched',
  on_hold: 'On hold',
  draft: 'Draft',
  pending: 'Pending',
  confirmed: 'Confirmed',
  payment_pending: 'Payment pending',
  ready_for_dispatch: 'Ready for dispatch',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  returned: 'Returned',
  unpaid: 'Unpaid',
  partial: 'Partial',
  paid: 'Paid',
  refunded: 'Refunded',
  queued: 'Queued',
  picking: 'Picking',
  packed: 'Packed',
  ready: 'Ready',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  failed: 'Failed',
  requested: 'Requested',
  approved: 'Approved',
  rejected: 'Rejected',
  received: 'Received',
  inspected: 'Inspected',
  restocked: 'Restocked',
  closed: 'Closed',
  processing: 'Processing',
  completed: 'Completed',

  active: 'Active',
  inactive: 'Inactive',
  invited: 'Invited',
  revoked: 'Revoked',
  expired: 'Expired',
  suspended: 'Suspended',
  archived: 'Archived',
  trial: 'Trial',
  past_due: 'Past due',
  denied: 'Denied',
  succeeded: 'Succeeded',
  exhausted: 'Exhausted',
};

interface Props {
  status: string;
  label?: string;
  className?: string;
}

export function StatusBadge({ status, label, className }: Props) {
  const variant = STATUS_TO_VARIANT[status] ?? 'muted';
  return (
    <Badge variant={variant} className={cn('font-medium', className)}>
      {label ?? LABELS[status] ?? status}
    </Badge>
  );
}
